import { useState } from 'react'

/**
 * Maintains insertion-order stability for items across re-fetches.
 * New items are appended at the end; removed items are cleaned up.
 * Order resets when scope changes (e.g., switching to a different month).
 *
 * Uses React's recommended "adjust state during render" pattern
 * instead of refs, which are unsafe to access during render.
 */
export function useStableOrder<T>(
  items: T[],
  getKey: (item: T) => string,
  scope: string,
): T[] {
  const [state, setState] = useState({ scope, order: new Map<string, number>() })

  let order = state.order

  if (state.scope !== scope) {
    // Scope changed — rebuild insertion order from scratch
    order = new Map<string, number>()
    items.forEach((item, i) => {
      const k = getKey(item)
      if (!order.has(k)) order.set(k, i)
    })
    setState({ scope, order })
  } else {
    // Same scope — update if items were added or removed
    const presentKeys = new Set(items.map(getKey))
    const hasNew = items.some(item => !order.has(getKey(item)))
    const hasStale = [...order.keys()].some(k => !presentKeys.has(k))

    if (hasNew || hasStale) {
      const next = new Map<string, number>()
      for (const [k, v] of order) {
        if (presentKeys.has(k)) next.set(k, v)
      }
      // Start new indices after the highest existing index to avoid collisions
      let nextIdx = 0
      for (const v of next.values()) {
        if (v >= nextIdx) nextIdx = v + 1
      }
      for (const item of items) {
        const k = getKey(item)
        if (!next.has(k)) next.set(k, nextIdx++)
      }
      order = next
      setState({ scope, order: next })
    }
  }

  return [...items].sort(
    (a, b) =>
      (order.get(getKey(a)) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(getKey(b)) ?? Number.MAX_SAFE_INTEGER),
  )
}
